import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'
import type { ITodolistTemplate, ITodolistTemplateTask } from '~/types/todolist-template'

import Button from '~/components/Buttons/Button'
import CreatableSelectInput from '~/components/Inputs/ReactHookForm/CreatableSelectInput'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import { FORM_ERRORS } from '~/const'
import { keys as todolistQueriesKeys, useUpdateTodolistTemplateMutation } from '~/queries/todolist-templates'
import { reactSelectMenuPositionDef } from '~/utils/options'
import { displayToast } from '~/utils/toast'
import { RequiredYupOption } from '~/utils/yup'

const MODAL_ID = 'set-todolist-template-task-modal'

export interface FormValues {
  description: string
  group_names: IOption<string>[]
  previous_steps: string[]
  title: string
}

const validationSchema = Yup.object({
  description: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
  group_names: Yup.array().of(RequiredYupOption).required(),
  previous_steps: Yup.array().of(Yup.string().trim().required()).required(),
  title: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
})

interface FormProps {
  onClose: VoidFunction
  onSuccess: VoidFunction
  task?: ITodolistTemplateTask
  todolistTemplate: ITodolistTemplate
}

const Form = ({ onClose, onSuccess, task, todolistTemplate }: FormProps): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = useForm<FormValues>({
    defaultValues: {
      description: task?.description || '',
      group_names: task?.group_names.map((g) => ({ label: g, value: g })) || [],
      previous_steps: task?.previous_steps || [],
      title: task?.title || '',
    },
    resolver: yupResolver(validationSchema),
  })

  const previousGroupsOptions = todolistTemplate.tasks
    .reduce((prevGroupNames, { group_names }) => [...new Set([...prevGroupNames, ...group_names])], [] as string[])
    .map((name) => ({ label: name, value: name }))

  /** Queries & Mutations */

  const { mutateAsync: updateTodolistTemplate } = useUpdateTodolistTemplateMutation()

  /** Handlers */

  const handleFormSubmit = async (values: FormValues) => {
    try {
      await updateTodolistTemplate({
        data: {
          tasks: task
            ? todolistTemplate.tasks.map((t) =>
                t.template_task_id === task.template_task_id
                  ? {
                      ...values,
                      group_names: [...new Set(values.group_names.map((g) => g.value))],
                      template_task_id: t.template_task_id,
                    }
                  : t
              )
            : [
                ...todolistTemplate.tasks,
                {
                  ...values,
                  group_names: [...new Set(values.group_names.map((g) => g.value))],
                  template_task_id: '',
                },
              ],
          template: todolistTemplate.template,
        },
        templateId: todolistTemplate.template.template_id,
      })
      await queryClient.invalidateQueries({
        queryKey: todolistQueriesKeys.details(todolistTemplate.template.template_id),
      })
      displayToast(task ? 'Task edited successfully' : 'Task added successfully', 'success')
      onSuccess()
    } catch (e) {
      captureException(e)
      displayToast('Task cannot be added!', 'error')
    }
  }

  /** Render */

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalBody className="set-todolist-template-modal__body">
        <RequiredInformation />
        <TextInput
          {...register('title')}
          error={errors.title?.message}
          label="Title"
          placeholder="Type a title"
          required
        />
        <TextInput
          {...register('description')}
          error={errors.description?.message}
          label="Description"
          placeholder="Type a description"
          required
        />
        <Controller
          control={control}
          name="group_names"
          render={({ field }) => (
            <CreatableSelectInput
              {...field}
              {...reactSelectMenuPositionDef(MODAL_ID)}
              error={errors.group_names?.message}
              isMulti
              menuPlacement="auto"
              menuShouldScrollIntoView
              options={previousGroupsOptions}
              placeholder="Enter the name of the teams and press Enter"
            />
          )}
        />
      </ModalBody>
      <ModalFooter>
        <Button ghost onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isSubmitting} type="submit">
          Save
        </Button>
      </ModalFooter>
    </form>
  )
}

interface ModalProps extends FormProps {
  isOpen: boolean
}

const SetTodolistTemplateTaskModal = ({ isOpen, onClose, ...props }: ModalProps): ReactElement => {
  return (
    <Modal className="set-todolist-template-task-modal" id={MODAL_ID} isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>Add a task</ModalHeader>
      <Form {...props} onClose={onClose} />
    </Modal>
  )
}

export default SetTodolistTemplateTaskModal
